/**
 * HumanOverrideControls — Sprint 7 · Project Symphony
 *
 * Owner may interrupt anytime. Pause / Resume / Manual Control /
 * Emergency Stop are first-class buttons per executive directive.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertOctagon, Pause, Play, RefreshCw } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import {
  emergencyStopAutopilot,
  enableAutopilot,
  pauseAutopilot,
  resumeAutopilot,
  type AutopilotState,
} from "../api";

export function HumanOverrideControls({
  state,
  onRunTick,
  runTickPending,
}: {
  state: AutopilotState;
  onRunTick: () => void;
  runTickPending: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["autopilot-dashboard"] });

  const pause = useMutation({ mutationFn: () => pauseAutopilot(), onSuccess: refresh });
  const resume = useMutation({ mutationFn: () => resumeAutopilot(), onSuccess: refresh });
  const enable = useMutation({ mutationFn: () => enableAutopilot(), onSuccess: refresh });
  const stop = useMutation({ mutationFn: () => emergencyStopAutopilot(), onSuccess: refresh });

  const isEnabled = state === "enabled";
  const isStopped = state === "stopped";

  return (
    <Card className="p-5" data-testid="human-override-controls">
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
        Human Override
      </p>
      <div className="flex flex-col gap-2">
        {isEnabled ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => pause.mutate()}
            disabled={pause.isPending}
            data-testid="autopilot-pause"
          >
            <Pause className="h-4 w-4 mr-2" aria-hidden />
            {pause.isPending ? "Pausing…" : "Pause Department"}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => (isStopped ? enable.mutate() : resume.mutate())}
            disabled={enable.isPending || resume.isPending}
            data-testid="autopilot-resume"
          >
            <Play className="h-4 w-4 mr-2" aria-hidden />
            {enable.isPending || resume.isPending ? "Resuming…" : "Resume Department"}
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={onRunTick}
          disabled={runTickPending || !isEnabled}
          data-testid="autopilot-manual-tick"
        >
          <RefreshCw className="h-4 w-4 mr-2" aria-hidden />
          {runTickPending ? "Running…" : "Manual Control · Run Now"}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => stop.mutate()}
          disabled={stop.isPending || isStopped}
          data-testid="autopilot-emergency-stop"
          className="text-rose-600 hover:text-rose-700"
        >
          <AlertOctagon className="h-4 w-4 mr-2" aria-hidden />
          Emergency Stop
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-3 leading-snug">
        Every autonomous action is reversible. Emergency Stop halts the
        department until you manually resume.
      </p>
    </Card>
  );
}
